type Change = {
    path: string;
    from: any;
    to: any;
};

function isObject(value: any): boolean {
    return value !== null && typeof value === 'object';
}

/**
 * Computes the deep differences between two objects up to a maximum depth of 3.
 *
 * Traverses both the `oldObj` and `newObj`, comparing their properties recursively.
 * If a difference is found (including added, removed, or changed properties), a `Change`
 * object is recorded with the property path, the old value, and the new value.
 *
 * - If the values are identical, no change is recorded.
 * - If either value is not an object, the change is recorded.
 * - The comparison stops at a depth of 3 to prevent excessive recursion.
 *
 * @param oldObj - The original object to compare.
 * @param newObj - The new object to compare against the original.
 * @returns An array of `Change` objects representing the differences between the two objects.
 */
export function getDeepDiff(oldObj: any, newObj: any): Change[] {
    const changes: Change[] = [];

    function compare(old: any, current: any, path = '', depth = 0) {
        // Stop if we've gone too deep
        if (depth > 3) return;

        // If values are identical, skip
        if (old === current) return;

        // If either isn't an object, record the change
        if (!isObject(old) || !isObject(current)) {
            changes.push({ path, from: old, to: current });
            return;
        }

        // Compare all keys from both objects
        const allKeys = new Set([...Object.keys(old), ...Object.keys(current)]);

        for (const key of allKeys) {
            const newPath = Array.isArray(old) ? `${path}[${key}]` :
                path ? `${path}.${key}` : key;
            compare(old[key], current[key], newPath, depth + 1);
        }
    }

    compare(oldObj, newObj);
    return changes;
}